/* ==========================================================================
   FlatWine — motion.js
   Reveals en scroll, texto cinético y estado del header.
   Sin dependencias. La referencia carga jQuery + ScrollReveal + anime.js;
   esto hace lo mismo en ~2 KB con IntersectionObserver.
   ========================================================================== */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     Texto cinético
     Parte la frase en letras envueltas en spans. El contenedor conserva la
     frase completa en aria-label y los spans van aria-hidden, de modo que
     lectores de pantalla y buscadores leen la frase, nunca letras sueltas.
     ---------------------------------------------------------------------- */
  function splitKinetic(el) {
    var text = el.textContent.trim();
    if (!text || el.dataset.split === 'done') return;

    el.setAttribute('aria-label', text);
    el.textContent = '';

    var words = text.split(/\s+/);
    var index = 0;

    words.forEach(function (word, wordIndex) {
      var wordEl = document.createElement('span');
      wordEl.className = 'kinetic__word';
      wordEl.setAttribute('aria-hidden', 'true');

      word.split('').forEach(function (char) {
        var charEl = document.createElement('span');
        charEl.className = 'kinetic__char';
        charEl.style.setProperty('--i', index++);
        charEl.textContent = char;
        wordEl.appendChild(charEl);
      });

      el.appendChild(wordEl);

      // Espacio real entre palabras para que el texto pueda romper línea.
      if (wordIndex < words.length - 1) {
        el.appendChild(document.createTextNode(' '));
      }
    });

    el.dataset.split = 'done';

    // Un titular cinético sin ancestro [data-reveal] nunca recibiría
    // .is-revealed y se quedaría invisible. Se hace auto-suficiente:
    // si nadie lo va a revelar, se revela solo.
    if (!el.closest('[data-reveal]')) {
      el.setAttribute('data-reveal', '');
    }
  }

  /* ------------------------------------------------------------------------
     Reveals
     ---------------------------------------------------------------------- */
  function initReveals() {
    var targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, {
      /* threshold 0: basta con que asome. Con 0.12 hacía falta ver el 12 %
         del elemento, y una rejilla más alta que ~8 pantallas nunca llegaba
         a eso: se quedaba invisible entera. Pasó en Merch con 48 productos,
         y en móvil, a una columna, bastaban muchos menos. */
      rootMargin: '0px 0px -12% 0px',
      threshold: 0
    });

    targets.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------------
     Header: alto real como custom property + estado "pegado"
     ---------------------------------------------------------------------- */
  /* El editor reemplaza el encabezado entero al cambiar el logotipo o una liga
     del menú, así que initHeader tiene que poder correr más de una vez. Antes
     no podía: cada pasada dejaba un listener de resize apuntando a un
     encabezado ya desconectado —fuga de memoria— y un observador vigilando un
     centinela muerto, con lo que la barra ya nunca se solidificaba al hacer
     scroll. Ahora se desmonta lo anterior primero. */
  var headerCleanup = null;

  function initHeader() {
    if (headerCleanup) { headerCleanup(); headerCleanup = null; }

    var header = document.querySelector('[data-header]');
    if (!header) return;

    function syncHeight() {
      document.documentElement.style.setProperty(
        '--header-h', header.offsetHeight + 'px'
      );
      /* La portada mide 100svh y sube el alto del header con un margen
         negativo, pero encima del header va la marquesina y nadie la
         descontaba: la portada acababa por debajo del pliegue y el indicador
         «Explorar» —lo único cuyo trabajo es decir que hay más abajo— quedaba
         entero fuera de pantalla en teléfono.

         Se mide el desplazamiento REAL de la portada en vez de sumar piezas:
         la marquesina, el margen negativo y cualquier cosa que se añada
         después ya están dentro de ese número, y no hay que mantener una
         cuenta aparte que se desincronice. */
      var portada = document.querySelector('.hero');
      var arriba = 0;
      if (portada) {
        portada.style.setProperty('--hero-offset', '0px');
        arriba = Math.max(0, Math.round(portada.getBoundingClientRect().top + window.scrollY));
        portada.style.removeProperty('--hero-offset');
      }
      document.documentElement.style.setProperty('--hero-offset', arriba + 'px');
    }

    syncHeight();
    window.addEventListener('resize', syncHeight, { passive: true });
    /* Al pegarse, el encabezado de escritorio encoge su padding (.is-stuck) y
       mide ~25 px menos, pero la altura sólo se recalculaba al cambiar el
       tamaño de la ventana. La barra de filtros seguía fija a la altura
       vieja y entre las dos quedaba un hueco por donde asomaban las fotos.
       Observar el propio encabezado lo sigue en cada cambio. */
    /* Durante la transición de padding el observador dispara en cada cuadro.
       Aquí sólo hace falta la altura: la posición de la portada no depende de
       ella —su margen negativo la compensa—, así que repetir la medición
       completa con getBoundingClientRect era forzar layout para obtener el
       mismo número. */
    var headerFrame = 0;
    var headerResize = 'ResizeObserver' in window ? new ResizeObserver(function () {
      cancelAnimationFrame(headerFrame);
      headerFrame = requestAnimationFrame(function () {
        document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
      });
    }) : null;
    if (headerResize) headerResize.observe(header);
    headerCleanup = function () {
      window.removeEventListener('resize', syncHeight);
      cancelAnimationFrame(headerFrame);
      if (headerResize) headerResize.disconnect();
    };

    // Sólo las páginas que abren con un hero a sangre pueden permitirse
    // un encabezado transparente. En las demás se queda sólido.
    /* Los seis lockups del header van en lazy para no bajarse los cinco que el
       CSS esconde. Cuatro de ellos los decide algo que ya existe antes de
       pintar —la media query de ancho y el data-theme que estampa el script
       en línea del <head>—, así que el navegador acierta solo.

       Los dos «sobre fotografía» no: los revela header--on-dark, que se añade
       AQUÍ, después de cargar. Para el lazy loader llegaron en display:none y
       ya los descartó, así que hay que pedírselos a mano o el logotipo de la
       portada aparece vacío. Reasignar src fuerza la petición en los
       navegadores donde cambiar loading a solas no basta. */
    function forzarCarga(img) {
      if (!img || (img.complete && img.naturalWidth > 0)) return;
      img.loading = 'eager';
      var src = img.getAttribute('src');
      if (src) img.setAttribute('src', src);
    }

    var hero = document.querySelector('.hero');
    if (hero) {
      // La portada de pie deja la barra en su sitio: ahí la imagen empieza
      // DEBAJO del encabezado, no detrás, y esa separación es justo lo que
      // distingue la composición. Superponerla le devolvía el logotipo
      // encima de la fotografía.
      if (!hero.classList.contains('hero--caption')) {
        header.classList.add('header--overlay');
        header.querySelectorAll('.header__logo--photo').forEach(forzarCarga);

        // Transparente no dice nada sobre el color de lo que hay detrás, y
        // ahí estaba el fallo: la portada tipográfica abre sobre un campo
        // CLARO y quiere texto oscuro, mientras que las multimedia abren
        // sobre fotografía oscura y lo quieren claro. Con una sola clase para
        // las dos situaciones, el menú desaparecía sobre la foto.
        if (!hero.classList.contains('hero--split')) {
          header.classList.add('header--on-dark');
        }
      }
    }

    // El header se solidifica una vez que el hero deja de cubrir la barra.
    var sentinel = document.querySelector('[data-header-sentinel]');
    if (!sentinel || !('IntersectionObserver' in window)) return;

    var stickyObserver = new IntersectionObserver(function (entries) {
      header.classList.toggle('is-stuck', !entries[0].isIntersecting);
    }, { threshold: 0 });
    stickyObserver.observe(sentinel);

    var anterior = headerCleanup;
    headerCleanup = function () {
      if (anterior) anterior();
      stickyObserver.disconnect();
    };
  }

  /* ------------------------------------------------------------------------
     Videos decorativos: sólo reproducen cuando están visibles. Con movimiento
     reducido se quedan pausados y la imagen de portada sigue comunicando.
     ---------------------------------------------------------------------- */
  function initAutoplayVideos(scope) {
    var videos = (scope || document).querySelectorAll('.js-autoplay-video');
    videos.forEach(function (video) {
      if (video.dataset.videoObserved === 'true') return;
      video.dataset.videoObserved = 'true';

      /* Con movimiento reducido no se reproduce; con ahorro de datos o
         conexión lenta tampoco, y además se le quita la fuente para que ni
         siquiera se descargue. El poster comunica igual, que es la razón por
         la que existe. */
      var con = navigator.connection;
      /* Safari no implementa navigator.connection, así que para buena parte
         del tráfico esta guarda no hace nada. No es un fallo —el video ya pesa
         283 KB— pero conviene que esté dicho y no parezca una promesa
         universal. */
      var ahorra = !!(con && (con.saveData ||
                              con.effectiveType === 'slow-2g' ||
                              con.effectiveType === '2g'));
      /* Y solo se quita si queda algo en su lugar. Sin poster, quitarlo deja
         un campo de color plano: peor que bajar el video. El poster solo se
         emite si la portada tiene imagen de respaldo, así que no se puede dar
         por hecho. */
      if (ahorra && !video.getAttribute('poster')) ahorra = false;
      if (reduced || ahorra || !('IntersectionObserver' in window)) {
        /* A propósito, pero en el editor parece un video roto: quien tiene
           «Reducir movimiento» activado en su sistema ve el póster fijo. */
        if (window.Shopify && window.Shopify.designMode) {
          console.info('[motion] el video no se reproduce: ' +
            (reduced ? 'el sistema pide reducir el movimiento.' :
             ahorra ? 'el visitante tiene ahorro de datos.' :
             'el navegador no soporta IntersectionObserver.'));
        }
        video.pause();
        if (ahorra) {
          video.removeAttribute('autoplay');
          video.querySelectorAll('source').forEach(function (s) { s.removeAttribute('src'); });
          video.removeAttribute('src');
          video.load();
        }
        return;
      }

      video.muted = true;
      var enPantalla = false;
      var esperandoGesto = false;

      /* Un play() rechazado ya no es definitivo. Chrome aborta (AbortError)
         el de una pestaña en segundo plano para ahorrar energía, y Safari con
         bajo consumo o autoplay restringido lo niega (NotAllowedError) hasta
         que el visitante interactúa. Antes ninguno de los dos se reintentaba y
         la portada se quedaba en el póster para siempre. Ahora se vuelve a
         pedir al regresar a la pestaña, cuando el video puede reproducirse y,
         si el navegador exige un gesto, con el primer toque o tecla. */
      function intentar() {
        /* El editor reemplaza secciones enteras: un video que ya no está en la
           página suelta su listener en vez de quedarse colgado del documento. */
        if (!video.isConnected) { document.removeEventListener('visibilitychange', intentar); return; }
        if (!enPantalla || document.hidden || !video.paused) return;
        var playPromise = video.play();
        if (!playPromise || typeof playPromise.catch !== 'function') return;
        playPromise.catch(function (err) {
          var nombre = err && err.name;
          if (nombre === 'AbortError') return;
          if (nombre === 'NotAllowedError') { esperarGesto(); return; }
          console.error('[motion] el video no pudo reproducirse', video.currentSrc || video.src, err);
        });
      }

      function esperarGesto() {
        if (esperandoGesto) return;
        esperandoGesto = true;
        /* pointerdown táctil no cuenta como gesto que habilite play(): un toque
           lo gastaba en un intento condenado. pointerup, touchend, click y
           keydown sí son activaciones de usuario. */
        var eventos = ['pointerup', 'touchend', 'click', 'keydown'];
        function alGesto() {
          eventos.forEach(function (ev) { document.removeEventListener(ev, alGesto); });
          esperandoGesto = false;
          intentar();
        }
        eventos.forEach(function (ev) { document.addEventListener(ev, alGesto, { passive: true }); });
      }

      document.addEventListener('visibilitychange', intentar);
      video.addEventListener('canplay', intentar);

      /* Un corte de red o un fallo de decodificación a media reproducción no
         rechaza ningún play(), y el error de un <source> no burbujea al video:
         sin estos dos listeners quedaba un cuadro congelado sin rastro. */
      function informarFallo() {
        console.error('[motion] el video falló', video.currentSrc || video.src, video.error && video.error.code);
      }
      video.addEventListener('error', informarFallo);
      var fuentes = video.querySelectorAll('source');
      if (fuentes.length) fuentes[fuentes.length - 1].addEventListener('error', informarFallo);

      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          enPantalla = entry.isIntersecting;
          if (enPantalla) intentar();
          else video.pause();
        });
      }, { threshold: 0.15 }).observe(video);
    });
  }

  /* Los videos informativos conservan controles y nunca arrancan solos, pero
     se pausan al salir de pantalla para evitar audio o consumo inesperado. */
  function initVisibilityVideos(scope) {
    var videos = (scope || document).querySelectorAll('.js-visibility-video');
    if (!('IntersectionObserver' in window)) return;

    videos.forEach(function (video) {
      if (video.dataset.visibilityObserved === 'true') return;
      video.dataset.visibilityObserved = 'true';

      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) video.pause();
        });
      }, { threshold: 0.05 }).observe(video);
    });
  }

  /* ------------------------------------------------------------------------
     Arranque
     ---------------------------------------------------------------------- */
  /* Marcado que llega DESPUÉS del arranque —recomendaciones traídas por fetch,
     una sección que reemplaza el editor— no pasa por initReveals, que junta
     sus objetivos una sola vez. Sin esto, base.css deja ese contenido en
     opacity:0 para siempre: el bloque ocupa su altura completa y no se ve
     nada. Se revela de golpe en vez de observarlo, igual que ya hacía el
     manejador del editor. */
  function hydrate(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-kinetic]').forEach(splitKinetic);
    if (root.matches && root.matches('[data-reveal]')) root.classList.add('is-revealed');
    root.querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-revealed');
    });
    initAutoplayVideos(root);
    initVisibilityVideos(root);
    dedupeStickers(root);
  }

  window.fwMotion = { hydrate: hydrate, initHeader: initHeader, dedupeStickers: dedupeStickers };

  /* ------------------------------------------------------------------------
     Stickers: nunca el mismo dibujo dos veces en una página. Cada sección se
     configura por separado y el pie y el cajón del carrito salen en todas,
     así que la repetición no se puede evitar desde Liquid.

     Gana el primero que SE VE, no el primero del documento: en teléfono los
     de esquina y los flotantes están ocultos por CSS, y quedarse con uno de
     ellos borraba el único visible. Con `scope` (marcado que llega después,
     como las recomendaciones) solo se quita lo recién llegado: lo que el
     visitante ya estaba viendo no desaparece de golpe.

     En el editor no se quita nada: se marca con una nota escrita, para que
     quien edita sepa cuál sobra en vez de preguntarse dónde fue.
     ---------------------------------------------------------------------- */
  function dedupeStickers(scope) {
    var editor = !!(window.Shopify && window.Shopify.designMode);
    var grupos = {};
    var claves = [];

    if (editor) {
      document.querySelectorAll('.sticker__dupe-note').forEach(function (nota) { nota.remove(); });
    }

    document.querySelectorAll('.sticker[data-sticker]').forEach(function (el) {
      if (editor) el.style.outline = '';
      var clave = el.getAttribute('data-sticker');
      if (!grupos[clave]) { grupos[clave] = []; claves.push(clave); }
      grupos[clave].push(el);
    });

    function visible(el) { return el.getClientRects().length > 0; }
    function llegoAhora(el) { return !!scope && scope !== document && scope.contains(el); }

    claves.forEach(function (clave) {
      var grupo = grupos[clave];
      if (grupo.length < 2) return;
      var queda = grupo.filter(function (el) { return visible(el) && !llegoAhora(el); })[0] ||
                  grupo.filter(visible)[0] ||
                  grupo[0];

      grupo.forEach(function (el) {
        if (el === queda) return;
        if (editor) {
          el.style.outline = '2px dashed #c0392b';
          var nota = document.createElement('span');
          nota.className = 'editor-note sticker__dupe-note';
          nota.textContent = 'Este dibujo ya sale en otra parte de la página; en la tienda no se mostrará aquí.';
          el.insertAdjacentElement('afterend', nota);
          return;
        }
        if (scope && !llegoAhora(el)) return;
        el.remove();
      });
    });
  }

  function init() {
    dedupeStickers();
    document.querySelectorAll('[data-kinetic]').forEach(splitKinetic);
    initReveals();
    initHeader();
    initAutoplayVideos(document);
    initVisibilityVideos(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    hydrate(event.target);
    /* initHeader se rehace SIEMPRE, no solo cuando lo reemplazado es el
       encabezado. --hero-offset mide lo que hay por encima de la portada, y
       la marquesina es su propia sección: editarla, ocultarla o alargarla
       dejaba la variable congelada y la portada descuadrada respecto al
       pliegue —que es justo lo que la variable existe para evitar—. La función
       ya se desmonta a sí misma antes de rehacerse, así que repetirla es
       barato y seguro. */
    initHeader();
  });

  /* Reordenar o quitar una sección cambia qué sticker sale primero. Sin volver
     a calcular, la nota del editor señalaba como sobrante uno que ya no lo era.
     El setTimeout deja que el editor termine de mover o retirar el marcado. */
  ['shopify:section:reorder', 'shopify:section:unload'].forEach(function (evento) {
    document.addEventListener(evento, function () {
      setTimeout(function () { dedupeStickers(); }, 0);
    });
  });
})();
